import { Component, type ErrorInfo, type ReactNode } from 'react'

import { ApiError } from '@/lib/api/problem'

interface RouteErrorBoundaryProps {
  readonly children: ReactNode
}

interface RouteErrorBoundaryState {
  readonly error: Error | null
}

/**
 * Bir route'un fırlattığını yakalar ve işlem numarasıyla birlikte gösterir
 * (`docs/spec/11-logging-observability.md §11`).
 *
 * Asıl mesele işlem numarası. O olmadan destek görüşmesi "bir ekranda, bir ara, bir şey ters
 * gitti" diye başlar ve sunucu loglarında gerçekten başarısız olan istek aranamaz. Onunla birlikte
 * tek bir dizge, kullanıcının baktığı ekranı onu açıklayan log satırına bağlar.
 *
 * Sınıf bileşeni, çünkü React'te hook karşılığı yok: hata sınırları hâlâ `componentDidCatch` ister.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  override state: RouteErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Yutulmuyor, raporlanıyor. Tek bir çağrının arkasında tutuldu ki bir toplayıcıya gönderilmeye
    // başlandığı gün tek yerde değişsin (`frontend-standards §7` — console.log merge edilmez).
    reportError(error, info)
  }

  override render(): ReactNode {
    const { error } = this.state

    if (error === null) {
      return this.props.children
    }

    return <ErrorScreen error={error} onRetry={() => this.setState({ error: null })} />
  }
}

interface ErrorScreenProps {
  readonly error: Error
  readonly onRetry: () => void
}

function ErrorScreen({ error, onRetry }: ErrorScreenProps) {
  const correlationId = error instanceof ApiError ? error.correlationId : null

  // Sunucunun mesajı okuyan kişi için yazılmıştır ve gösterilmesi güvenlidir. Geri kalan her şeye
  // genel bir satır düşer: beklenmeyen bir istisnanın mesajı ekrana değil loga aittir
  // (`backend-standards §5a.8` — aynı kural, bu uçtan bakınca).
  const message = error instanceof ApiError ? error.message : 'Beklenmeyen bir hata oluştu.'

  return (
    <section
      role="alert"
      className="mx-auto flex max-w-lg flex-col items-start gap-4 p-8 text-left"
    >
      <h1 className="text-xl font-semibold">Bir hata oluştu</h1>
      <p className="text-muted-foreground">{message}</p>

      {correlationId !== null && correlationId !== '' && (
        <p className="text-muted-foreground text-sm">
          İşlem numarası: <code className="font-mono">{correlationId}</code>
          <br />
          Destek ekibiyle görüşürken bu numarayı iletin.
        </p>
      )}

      <button
        type="button"
        onClick={onRetry}
        className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium"
      >
        Tekrar dene
      </button>
    </section>
  )
}

function reportError(error: Error, info: ErrorInfo): void {
  // SPA'nın yakalanmış bir hatayı raporladığı tek yer, bilinçli olarak burası. Bir toplayıcı ucu
  // (`LogEvents.ClientError`) planlı; o gelene kadar bu, hatayı sessizce atmak yerine tarayıcıda
  // görünür tutar.
  globalThis.console.error('[MersisPortal]', error, info.componentStack)
}
